import type { UseInfiniteQueryOptions, UseQueryOptions } from 'react-query';
import { useInfiniteQuery, useQuery } from 'react-query';

import { LEADS_PAGE_SIZE } from 'api/constants/leads';
import type { IFiltersAPI } from 'components/Filter/utils/filters';

import { apiClient } from '../apiClient';
import type { ILead, ILeadsSortParams, Status } from '../types/lead';
import type { IRequestException } from '../types/requestException';

interface ILeadsProps {
    page?: number;
    pageSize?: number;
    status?: Status;
    search?: string;
    sortParams?: ILeadsSortParams;
    filterQueryParams?: IFiltersAPI;
}

export interface IGetLeadsProps
    extends UseQueryOptions<ILead[], IRequestException>,
        ILeadsProps {}

export interface IGetLeadsInfiniteProps
    extends UseInfiniteQueryOptions<ILead[], IRequestException>,
        ILeadsProps {}

export const getLeads = async ({
    status,
    search,
    page = 0,
    sortParams,
    pageSize = LEADS_PAGE_SIZE,
    filterQueryParams,
}: IGetLeadsProps) => {
    const params = {
        status,
        search,
        pageSize,
        page,
        orderBy: sortParams?.sortBy,
        orderDirection: sortParams?.sortOrder,
        ...filterQueryParams,
    };

    const response = await apiClient.post('property-leads', {
        json: {
            ...params,
        },
    });
    return (await response.json()) as ILead[];
};

export const useGetLeadsQuery = ({
    status,
    page = 0,
    pageSize = LEADS_PAGE_SIZE,
    filterQueryParams,
    ...options
}: IGetLeadsProps) => {
    return useQuery<ILead[], IRequestException>(
        ['property-leads', { status, page, pageSize }],
        async () =>
            await getLeads({ status, page, pageSize, filterQueryParams }),
        {
            ...options,
        }
    );
};

export const useGetLeadsInfiniteQuery = ({
    status,
    search,
    sortParams,
    pageSize = LEADS_PAGE_SIZE,
    filterQueryParams,
    ...options
}: IGetLeadsInfiniteProps) => {
    return useInfiniteQuery<ILead[], IRequestException>(
        [
            'infinitePropertyLeads',
            {
                status,
                pageSize,
                sortParams,
                search,
                ...filterQueryParams,
            },
        ],
        async ({ pageParam = 0 }) =>
            await getLeads({
                status,
                search,
                pageSize,
                sortParams,
                page: pageParam,
                filterQueryParams,
            }),
        {
            getNextPageParam: (lastPage, pages) =>
                lastPage.length === LEADS_PAGE_SIZE ? pages.length : undefined,
            ...options,
        }
    );
};
